/**
 * Мост в Electron.
 *
 * Единственное место, где веб-приложение знает о существовании десктоп-оболочки.
 * Всё остальное работает одинаково в браузере и в Electron — именно это
 * ограничение делает замену Electron на собственный CEF-клиент работой в одном
 * каталоге (ARCHITECTURE.md §12).
 *
 * Отсутствие моста означает «работаем в браузере», а не поломку.
 */

export interface DesktopTabInfo {
  id: number;
  title: string;
  url: string;
  canGoBack: boolean;
  canGoForward: boolean;
  loading: boolean;
  /**
   * Показывается ли вкладка сейчас.
   *
   * Читать нужно защищённо: оболочка обновляется отдельно от интерфейса, и
   * предыдущая версия поля не присылает.
   */
  active?: boolean;
  /** Закреплённая вкладка. Уезжает в начало полосы и не закрывается крестиком. */
  pinned?: boolean;
  /** Значок сайта. Отсутствует у старой оболочки и пока страница его не прислала. */
  favicon?: string;
  /**
   * Раздел, которому вкладка принадлежит лично.
   *
   * Заполнен у встроенных разделов: их вкладка в общей полосе «Браузера» не
   * показывается. Отсутствует у старой оболочки — читать защищённо.
   */
  exclusiveTo?: string;
}

export interface DesktopBridge {
  /** Открыть URL в новой вкладке корпоративного браузера. */
  openTab(url: string): void;
  /**
   * Сообщить оболочке пару для входа в рабочую систему.
   *
   * Оболочка подставит её, когда система покажет форму входа, — в том числе
   * когда она выбросит сотрудника из сессии посреди дня.
   *
   * Метода нет у старой оболочки, поэтому вызывать его нужно защищённо.
   */
  setSectionLogin?(payload: {
    url: string;
    login: string;
    password: string;
    /** Необязательны: обычную форму оболочка находит сама. */
    loginSelector?: string | null;
    passwordSelector?: string | null;
    submitSelector?: string | null;
    /**
     * Аккаунт площадки (Airbnb, Booking): вход в два шага и соседние домены
     * площадки. Оболочка до 0.6.0 поле не знает и входит по-старому.
     */
    twoStep?: boolean;
  }): void;
  /** Забыть пару входа. */
  forgetSectionLogin?(url: string): void;
  /**
   * Каким рабочим системам разрешены оповещения.
   *
   * Полный список при каждом изменении, а не «включи вот этот»: так состояние
   * оболочки не может разойтись с настройками, что бы ни потерялось по дороге.
   */
  setNotificationOrigins?(urls: string[]): void;
  /**
   * Показать пробное оповещение.
   *
   * Нужно для разбора «оповещения не приходят»: если пробное видно, значит
   * система приложение пропускает, и причина на стороне рабочего сайта.
   */
  testNotification?(): Promise<boolean>;
  pinTab(id: number, pinned: boolean): void;
  /** Сообщить оболочке вошедшего сотрудника: от этого зависит его профиль браузера. */
  setProfile(userId: string): void;
  /** Принудительно перевести активную вкладку на адрес. */
  navigateTab(url: string): void;
  closeTab(id: number): void;
  /**
   * Показать вкладку раздела по адресу. Идемпотентно.
   *
   * `exclusiveTo` закрепляет вкладку за разделом: в полосе «Браузера» её не
   * будет. Старая оболочка второй довод игнорирует.
   */
  showSection(url: string, exclusiveTo?: string): void;
  activateTab(id: number): void;
  /** Скрыть вкладки при уходе в системный раздел. */
  hideTabs(): void;
  /**
   * Выход сотрудника: закрыть его вкладки, забыть пароли разделов и адреса
   * оповещений, чтобы следующему за тем же окном ничего не досталось.
   * Необязательный: у уже установленных старых оболочек его нет.
   */
  resetProfile?(): void;
  /** Вкладка с собственными cookie — аккаунт площадки (оболочка 0.4.0+). */
  showIsolated?(url: string, key: string): void;
  /**
   * Данные открытой страницы: адрес, заголовок, фото, город (0.4.0+); текст,
   * JSON-LD, ссылки и заголовки для автозаписи брони — с 0.5.0, у старой
   * оболочки этих полей нет.
   */
  capturePage?(): Promise<{
    url: string;
    title: string;
    image: string | null;
    city: string | null;
    country: string | null;
    text?: string;
    jsonLd?: string[];
    links?: { href: string; text: string }[];
    headings?: string[];
  } | null>;
  /**
   * Область, куда оболочка кладёт содержимое вкладки.
   *
   * Нативная вкладка лежит поверх страницы и её раскладки не знает: область
   * считает страница и присылает сюда.
   */
  setTabBounds(rect: { x: number; y: number; width: number; height: number }): void;
  goBack(): void;
  goForward(): void;
  reloadTab(): void;
  onTabs(handler: (tabs: DesktopTabInfo[]) => void): () => void;
  getVersion(): string;
  /** Профиль текущего пользователя: cookies и storage изолированы по нему. */
  getProfileKey(): string;
  setTheme(theme: 'light' | 'dark' | 'system'): void;
}

declare global {
  interface Window {
    __tw_desktop?: DesktopBridge;
  }
}

export function desktop(): DesktopBridge | null {
  return window.__tw_desktop ?? null;
}

export function isDesktop(): boolean {
  return window.__tw_desktop !== undefined;
}

/**
 * Открытие внешней ссылки.
 *
 * В Electron ссылка уходит в изолированную вкладку с применёнными политиками
 * (whitelist доменов, запрет загрузок и т.п.). В браузере — обычный
 * `noopener,noreferrer`: без них открытая страница получает доступ к
 * `window.opener` и может подменить наше окно.
 */
export function openExternal(url: string): void {
  const bridge = desktop();
  if (bridge) {
    bridge.openTab(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
